<template>
  <div
    ref="rootEl"
    class="rf-input relative inline-flex h-fit flex-col gap-1"
    :class="[
      typeClasses[variant],
      size && sizeClasses[size],
      autoWidth && utilityClasses.autoWidth,
    ]"
  >
    <slot name="label">
      <label
        v-if="$slots.title || title"
        :for="labelFor || undefined"
        class="text-body text-black-2 font-semibold!"
      >
        <slot name="title" />
        <template v-if="!$slots.title && title">
          {{ title }}:<span v-if="requiredTitle" class="text-vermillion-50">*</span>
        </template>
      </label>
    </slot>

    <span
      class="bg-white-1 rf-input--wrapper flex w-full items-center overflow-hidden rounded-lg"
      :class="wrapperClass"
    >
      <slot />
      <button
        v-if="clearable && hasValue"
        type="button"
        aria-label="Clear"
        class="rf-input--clear mr-3 inline-flex shrink-0 items-center justify-center"
        @mousedown.prevent
        @click="emit('clear')"
      >
        <RfCloseIcon class="fill-beige-50 h-5 w-5" />
      </button>
    </span>

    <div v-if="errorMessage || hintMessage || counterText" class="flex justify-between gap-2">
      <Transition mode="out-in" tag="span">
        <span
          v-if="errorMessage"
          :id="messageId"
          role="alert"
          class="text-error-1 text-justify text-xs"
          >{{ errorMessage }}</span
        >
        <span v-else-if="hintMessage" :id="messageId" class="text-beige-50 text-justify text-xs">
          {{ hintMessage }}
        </span>
        <span v-else></span>
      </Transition>
      <span v-if="counterText" class="text-beige-50 shrink-0 text-xs whitespace-nowrap">
        {{ counterText }}
      </span>
    </div>
  </div>
</template>

<script>
const typeClasses = {
  default: "rf-input--default",
  outlined:
    "rf-input--outlined [&_.rf-input--wrapper]:border [&_.rf-input--wrapper]:border-strokes [&_.rf-input--wrapper]:border-solid",
};
const sizeClasses = { small: "rf-input--sm", base: "rf-input--base", large: "rf-input--lg" };
const utilityClasses = { autoWidth: "rf-input--auto-width" };
</script>

<script setup>
import { computed, ref, getCurrentInstance } from "vue";
import RfCloseIcon from "../icons/RfCloseIcon.vue";

const props = defineProps({
  title: { type: String, default: "" },
  requiredTitle: { type: Boolean, default: false },
  labelFor: { type: String, default: null },
  variant: {
    type: String,
    default: "default",
    validator: v => Object.keys(typeClasses).includes(v),
  },
  size: {
    type: String,
    default: null,
    validator: v => !v || Object.keys(sizeClasses).includes(v),
  },
  autoWidth: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
  hintMessage: { type: String, default: "" },
  wrapperClass: { type: [String, Array, Object], default: "" },
  clearable: { type: Boolean, default: false },
  hasValue: { type: Boolean, default: false },
  counter: { type: [Boolean, String, Number], default: false },
  maxlength: { type: [String, Number], default: null },
  value: { type: [String, Number], default: "" },
});

const emit = defineEmits(["clear"]);

const { uid } = getCurrentInstance();
const messageId = computed(() =>
  props.errorMessage || props.hintMessage ? `message${uid}` : null,
);
const counterText = computed(() => {
  if (!props.counter) return null;
  const length = String(props.value ?? "").length;
  const max = typeof props.counter === "boolean" ? props.maxlength : props.counter;
  return max ? `${length} / ${max}` : `${length}`;
});

const rootEl = ref();
defineExpose({
  get el() {
    return rootEl.value;
  },
  messageId,
});
</script>

<style lang="scss">
.rf-input {
  &.rf-input--auto-width.rf-input--auto-width {
    .rf-input--wrapper {
      min-width: 1px;
      width: auto;
    }
  }
  &.rf-input--sm {
    .rf-input--wrapper {
      min-height: 32px;
      min-width: 112px;
    }
  }
  &.rf-input--base {
    .rf-input--wrapper {
      min-height: 40px;
      min-width: 332px;
    }
  }
  &.rf-input--lg {
    .rf-input--wrapper {
      min-height: 56px;
      min-width: 332px;
    }
  }
}

// Outlines the whole field on keyboard focus of its native control or its
// RfSelect/RfFilter trigger (.rf-button) — not prepend/append buttons like a
// password-visibility toggle, which keep their own focus indicator.
.rf-input--wrapper:has(input:focus-visible, textarea:focus-visible, .rf-button:focus-visible) {
  outline: 2px solid var(--color-blue-50);
  outline-offset: 2px;

  input:focus-visible,
  textarea:focus-visible {
    box-shadow: none;
  }

  .rf-button:focus-visible {
    outline: none;
  }
}
</style>
